import React from 'react';
import {
  ChartBarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  BugAntIcon,
  CubeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

import RiskDashboard from './RiskDashboard';
import RegulatoryReports from './RegulatoryReports';
import RemediationPunchList from './RemediationPunchList';
import ReportingDashboard from './ReportingDashboard';
import WorkflowList from './WorkflowList';

/**
 * ContextPanel
 *
 * Right-hand pane that displays a contextual visualization based on the
 * intent of the most recent assistant response.
 *
 * Props:
 *   intent: string | null   - intent from latest response (e.g., 'risk_assessment')
 *   metadata: object | null - full response payload (workflow_id, entities, etc.)
 *   customerInfo: object    - current customer info, in case panels need it
 */
export default function ContextPanel({ intent, metadata, customerInfo }) {
  // Empty state — no intent yet (user just landed here)
  if (!intent) {
    return <WelcomeState customerInfo={customerInfo} />;
  }

  // Map intent to component. When we don't have a real component yet,
  // we fall back to a Placeholder that explains what would normally render here.
  const view = renderForIntent(intent, metadata);

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header bar showing the active context */}
      <ContextHeader intent={intent} metadata={metadata} />

      {/* The actual visualization */}
      <div className="mt-4">{view}</div>
    </div>
  );
}

// ─── Intent → component routing ──────────────────────────────────────────────

function renderForIntent(intent, metadata) {
  switch (intent) {
    case 'risk_assessment':
      return <RiskDashboard />;

    case 'compliance_status':
    case 'compliance_audit':
      return <RegulatoryReports />;

    case 'vulnerability_assessment':
    case 'remediation_planning':
      return <RemediationPunchList />;

    case 'report_generation':
      return <ReportingDashboard />;

    case 'security_assessment':
    case 'general_status':
      return <WorkflowList />;

    // Intents we recognize but don't have a dedicated component for yet
    case 'threat_intelligence':
      return (
        <Placeholder
          icon={ExclamationTriangleIcon}
          title="Threat Intelligence"
          message="A dedicated threat intelligence view is in development. The full analysis is in the chat response on the left."
        />
      );

    case 'incident_response':
      return (
        <Placeholder
          icon={BugAntIcon}
          title="Incident Response"
          message="A dedicated incident response view is in development. The full incident details and recommended playbooks are in the chat response on the left."
        />
      );

    case 'policy_analysis':
      return (
        <Placeholder
          icon={DocumentTextIcon}
          title="Policy Analysis"
          message="A dedicated policy analysis view is in development. The full policy gap analysis is in the chat response on the left."
        />
      );

    case 'asset_inventory':
      return (
        <Placeholder
          icon={CubeIcon}
          title="Asset Inventory"
          message="A dedicated asset inventory view is in development. The asset summary is in the chat response on the left."
        />
      );

    default:
      // Unknown intent — show the chat metadata so the user can still see what happened
      return (
        <Placeholder
          icon={ChatBubbleLeftRightIcon}
          title="Response Details"
          message={
            metadata?.workflow_id
              ? `Your request created workflow ${metadata.workflow_id}. The full response is in the chat on the left.`
              : 'See the response in the chat on the left.'
          }
        />
      );
  }
}

// ─── Header bar ──────────────────────────────────────────────────────────────

function ContextHeader({ intent, metadata }) {
  const { icon: Icon, label } = headerForIntent(intent);

  return (
    <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3">
      <div className="flex items-center space-x-3">
        <Icon className="h-5 w-5 text-primary-400" />
        <div>
          <h2 className="text-sm font-semibold text-white">{label}</h2>
          {metadata?.confidence !== undefined && (
            <p className="text-xs text-gray-400">
              Detected from your message · confidence {Math.round(metadata.confidence * 100)}%
            </p>
          )}
        </div>
      </div>
      {metadata?.workflow_id && (
        <span className="text-xs text-gray-500 font-mono">
          {metadata.workflow_id}
        </span>
      )}
    </div>
  );
}

function headerForIntent(intent) {
  const map = {
    risk_assessment: { icon: ChartBarIcon, label: 'Risk Dashboard' },
    compliance_status: { icon: ShieldCheckIcon, label: 'Compliance Status' },
    compliance_audit: { icon: ShieldCheckIcon, label: 'Compliance Audit' },
    vulnerability_assessment: { icon: BugAntIcon, label: 'Vulnerabilities' },
    remediation_planning: { icon: WrenchScrewdriverIcon, label: 'Remediation Plan' },
    report_generation: { icon: DocumentTextIcon, label: 'Reports' },
    security_assessment: { icon: ClipboardDocumentListIcon, label: 'Security Assessment' },
    general_status: { icon: ClipboardDocumentListIcon, label: 'Overview' },
    threat_intelligence: { icon: ExclamationTriangleIcon, label: 'Threat Intelligence' },
    incident_response: { icon: BugAntIcon, label: 'Incident Response' },
    policy_analysis: { icon: DocumentTextIcon, label: 'Policy Analysis' },
    asset_inventory: { icon: CubeIcon, label: 'Asset Inventory' },
  };

  return (
    map[intent] || {
      icon: ChatBubbleLeftRightIcon,
      label: intent ? intent.replace(/_/g, ' ') : 'Response',
    }
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function WelcomeState({ customerInfo }) {
  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600/10 border border-primary-600/30 rounded-full mb-4">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-primary-400" />
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">
          {customerInfo?.company_name
            ? `Welcome, ${customerInfo.company_name}`
            : 'Welcome to CISO Advisor'}
        </h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Ask a question on the left to get started. As you chat, the right pane
          will update with relevant dashboards, charts, and reports based on
          what you're asking about — risk, compliance, vulnerabilities, remediation,
          and more.
        </p>
      </div>
    </div>
  );
}

// ─── Placeholder for intents without a dedicated component yet ───────────────

function Placeholder({ icon: Icon, title, message }) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-700 rounded-full mb-4">
        <Icon className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed max-w-md mx-auto">
        {message}
      </p>
    </div>
  );
}
